import { getLocalStorage } from "../../utils/localStorage";

/**
 * Uplifted pieces of `data/constants` — the file exists in BOTH apps
 * (`apps/web/src/data/constants.ts`, `apps/mobile/src/data/constants.ts`).
 *
 * This folder MIRRORS the app file and holds only what has actually been needed so far.
 * `shared.ts` holds what the apps agree on — currently `ORDER_SOURCE`, read by web's divergent
 * `ITEMSTATUS.getAllInStockItems`. Symbols the two apps define DIFFERENTLY live in the platform
 * files instead (today: `NOTIFICATION_VARIABLES`, web 11 keys vs mobile 5). Add the next
 * required symbol to whichever of the three is correct, rather than creating a per-symbol
 * module.
 */

/**
 * `sale.itemLabelPrintingStatus` as the server spells it. Gates the "Labels Already Printed"
 * reprint confirmation on both order screens.
 *
 * Uplifted 2026-09-12 from `apps/web/src/data/constants.ts`, which is the only place it was
 * ever declared — mobile compared against a bare `'Printed'` literal. One definition now, in
 * `shared.ts` because the wire value cannot differ by platform.
 *
 * ⚠️ Only `Printed` is named because only `Printed` is branched on. The server also returns
 * `Pending` / `Failed` on individual label infos (see web's poller), but no screen reads those.
 */
export const ITEM_LABEL_PRINTING_STATUSES = {
  PRINTED: "Printed",
};

/**
 * WEB-only today: `ORDER_SOURCE` does not exist anywhere in apps/mobile. It lives in shared.ts
 * (not index.desktop.ts) because it is inert data with no platform behaviour — mobile simply
 * never imports it, and its own `data/constants` is left untouched.
 */
export const ORDER_SOURCE = {
  ALL: 'all',
  ONLINE: 'online',
  OFFLINE: 'offline',
  LOCALSTORE_KEY: 'orderSource',
};

/**
 * Identical in both apps (web `data/constants.ts:329`, mobile `:273`) — same name, same value.
 * Read by `helpers/saleHelpers.showToastFromApi`'s suppression list, where mobile had spelled
 * the same string as a literal.
 */
export const REST_SALE_BACK_ITEMS_NOT_PROCESSED = 'REST_SALE_BACK_ITEMS_NOT_PROCESSED';

/** Identical in both apps. Read by `catalogueSlice`'s category filtering on both platforms. */
export const CATEGORY_TYPE = {
  CATALOG: 'catalog',
  INVENTORY: 'inventory',
} as const;

/**
 * HTTP verbs used to build request configs. Identical in both apps (web spells the values with
 * double quotes, mobile single — same strings). Read by the `loyalty`, `notifications` and
 * `business` sagas.
 */
export const METHODS = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
};

/** Identical in both apps (web `data/constants.ts:13`, mobile `:8`). */
export const DISCOUNT_TYPES = {
  absolute: "Absolute",
  percentage: "Percentage",
  voucher: "Voucher",
};

/** Identical in both apps (web `:19`, mobile `:14`). */
export const DISCOUNT_MODES = {
  manual: "Manual",
  auto: "Auto",
  coupon: "Coupon",
};

/** Identical in both apps (web `:323`, mobile `:244`). A sale-item error code. */
export const INSUFFICIENT_STOCK = "INSUFFICIENT_STOCK";

/** A sale-item warning code. Web's (mobile defined none); shared since My Orders v2's `holdOrder` reads it on both. */
export const ITEM_SOLD_OUT = "ITEM_SOLD_OUT";

/** Web's back-order message for a sale with out-of-stock items; shared since My Orders v2's back-order sheet shows it on both. */
export const ITEM_OOS = "Sale contains some items which are not in stock, please increase the stock level of the item to proceed";

/**
 * How a login was initiated. **Byte-identical in both apps, including the derived type.**
 *
 * ⚠️ `as const` IS carried over here, unlike `DISCOUNT_APPROVAL_STAGE`: `LoginTypes` below is
 * derived from it and IS used as a type (`getLoginMetaInfo({ loginType }: {loginType?: LoginTypes})`
 * on both platforms), so widening the values to `string` would break those signatures.
 */
export const LOGIN_TYPES = {
  drkLogin: "drkLogin",
} as const;
type LoginTypeKeys = keyof typeof LOGIN_TYPES;
export type LoginTypes = (typeof LOGIN_TYPES)[LoginTypeKeys];

/** The two my-orders channels, as v1's `selectedChannel` spells them. Identical in both apps. */
export const DELIVERY = "Delivery";
export const INSTORE = "instore";

/** Identical in both apps; read by the shared order card (2026-09-15). */
export const CHANNEL_TYPE_POS = "POS";
export const DOTPE_SOURCE_TYPES = ["DotPePickup", "DotPeDelivery", "dotpe", "DotPeDine-in"];

/** Delivery-as-a-service event-log statuses and the rider-search note (2026-09-15). Identical in both apps. */
export const DAAS_CONSTANTS = {
  RIDER_ATTEMPTED: "RiderAssignAttempt",
  RIDER_ASSIGNED: "RiderAssigned",
  RIDER_ARRIVED: "RiderArrived",
  RIDER_CANCELLED: "RiderCancelled",
  RIDER_DISPATCHED: "Dispatched",
  ORDER_CREATED: "Created",
  DELIVERED: "Delivered",
};
// ⚠️ NOT `as const` (mobile's spelling): web's RiderStatus builds `{ [DAAS_CONSTANTS.X]: … }` maps
// and indexes them with `any`, which only compiles while the values are `string`, not literals.
export const ASSIGN_NOTE = "Searching for rider";
// moved from web's data/constants.ts 2026-09-16 (reject flow sheet): the masked-number request's action per aggregator
export const SWIGGY_CONTACT_KEY = "GET_CUSTOMER_MASKED_PHONE_NUMBER";
export const ZOMATO_CONTACT_KEY = "GET_CONTACT_DETAILS";
export const OWNLY_CONTACT_KEY = "GET_CONTACT_DETAILS";
export const SELF_DELIVERY = "selfDelivery";

/**
 * `Discount` vs `Deal` — the two discount CLASSES the sale record distinguishes.
 *
 * Identical in both apps (web `data/constants.ts:6`, mobile `:3`; mobile only spells the values
 * with single quotes). Read by both apps' `itemLevelDiscount`, `DiscountDeals` and
 * `featureRestrictionHelper`, and by core's `dotpeOfferReleaseEffect` to recognise a DotPe CRM
 * discount.
 */
export const DISCOUNT_CLASS_TYPES = {
  discount: "Discount",
  deal: "Deal",
} as const;

/**
 * Which suggestive-selling flow this terminal runs.
 *
 * SHARED as of the Suggestive Selling convergence. It used to be web-only, because only web had a
 * V2 engine; both platforms now run the same flow, the same slice actions and the same saga, so
 * the switch that picks the engine version has to be visible to both.
 *
 * ⚠️ **Read at MODULE-EVAL time**, which is why it is a boolean const rather than a function:
 * flipping the flow requires a restart, and the app's own comment says so. Safe on both platforms
 * — web's `getLocalStorage` touches the browser's synchronous `localStorage` and mobile's touches
 * a synchronous MMKV instance; neither goes through the effects bridge, so there is no boot-order
 * dependency, and `utils/localStorage` imports nothing that leads back here.
 */
export const SUGGESTIVE_SELLING_FLOW_KEY = "suggestiveSellingFlow";

/**
 * ⚠️ The try/catch is NOT defensive noise — it is what keeps this module importable OUTSIDE an
 * app. A module-eval read reaches the platform store the instant anything in the import graph
 * touches `data/constants`, and jest's `testEnvironment: "node"` has no `localStorage` at all, so
 * without it every core suite that transitively imports this file (`ordering/cart`,
 * `catalog`, …) dies on `ReferenceError: localStorage is not defined` before its first
 * assertion. Falling through to `null` selects V2 — the same answer an unset key gives, so no
 * app-side behaviour changes.
 */
const suggestiveSellingFlow = (() => {
  try {
    return getLocalStorage(SUGGESTIVE_SELLING_FLOW_KEY);
  } catch {
    return null;
  }
})();

/** V2 when unset or "v2"; set `suggestiveSellingFlow` to "v1" and restart for V1. */
export const IS_SUGGESTIVE_SELLING_V2_ENABLED =
  !suggestiveSellingFlow || suggestiveSellingFlow === "v2";

/**
 * External loyalty/CRM partners, the partner-CLASS arrays, and the program type the POS can
 * redeem points against.
 *
 * **Neither app's mobile file defines any of this group** (grepped, per hazard 32) — they live
 * in `shared.ts` rather than `index.desktop.ts` for the same reason `ORDER_SOURCE` does: inert
 * data with no platform behaviour. Being `.desktop`-only was want of a mobile consumer, not a
 * statement that this is a web concern (hazard 32's distinction), and a string map has no
 * platform half to split.
 *
 * TWO independent cross-platform callers pulled this group over within a day of each other:
 *   - 2026-09-04, the kiosk's contact flow — it needs `DOTPE` to decide whether an OTP step is
 *     expected, on both platforms. That moved the base map.
 *   - 2026-09-05, `packages/core/loyalty` — a platform-neutral flow whose rules classify
 *     providers and gate POS redemption, so a `.desktop`-only home would have pinned the whole
 *     flow to one platform. That brought the `_C1` / `_C2` / `_C3` arrays,
 *     `EXTERNAL_LOYALTY_PROVIDER_METAIDS` and `LOYALTY_PROGRAM_TYPE` across with it.
 *
 * `LOYALTY_PROGRAM_TYPE` came from `apps/web/src/data/constants.ts`; that file re-exports it.
 * Web importers are unchanged — `index.desktop.ts` re-exports `./shared`.
 */
/**
 * Why a customer cannot be changed right now.
 *
 * Uplifted from `apps/web/src/data/constants.ts` (`CUSTOMER_EDIT_INFO_FOR_LOYALTY`) on
 * 2026-09-09, when `apps/mobile` gained the gate that shows it. `blockCustomerChangeIfLocked`
 * in `helpers/loyaltyHelper` is the only thing that raises it — one sentence, one raiser, so the
 * two platforms cannot word the refusal differently.
 */
export const CUSTOMER_EDIT_INFO_FOR_LOYALTY =
  "Customer has participated in loyalty redemption and hence cannot be changed.";

export const EXTERNAL_LOYALTY_PARTNERS = {
  XENO: "xeno",
  DOTPE: "dotpe",
  REELO: "reelo",
  BLUETOKAI: "bluetokai",
} as const;

/**
 * Partners treated as "external loyalty" anywhere in the discount flow — the offers fetch, the
 * partner apply and the removal unblock all hang off this list.
 *
 * ⚠️ **DotPe joined on 2026-09-09 (DM-146).** It had been class 3 only, so its CRM coupons
 * travelled the ordinary Rista-coupon path and removing one never released it server-side.
 * `packages/core/discounts/providers/dotpe.ts` predicted exactly this correction and named
 * `isExternalLoyalty: true` as the whole fix; that profile — and `requiresCouponUnblock` beside
 * it — moved with this line, because the drift tests tie the two descriptions together.
 */
export const EXTERNAL_LOYALTY_PROVIDER_METAIDS: readonly string[] = [
  EXTERNAL_LOYALTY_PARTNERS.XENO,
  EXTERNAL_LOYALTY_PARTNERS.BLUETOKAI,
  EXTERNAL_LOYALTY_PARTNERS.DOTPE,
];

/** Class-1 partners — the ones whose coupons must be unblocked server-side on removal. */
export const EXTERNAL_LOYALTY_PARTNERS_C1: readonly string[] = [
  EXTERNAL_LOYALTY_PARTNERS.XENO,
];

/** Class-2 partners. */
export const EXTERNAL_LOYALTY_PARTNERS_C2: readonly string[] = [
  EXTERNAL_LOYALTY_PARTNERS.BLUETOKAI
];

/** Class-3 partners. */
export const EXTERNAL_LOYALTY_PARTNERS_C3: readonly string[] = [
  EXTERNAL_LOYALTY_PARTNERS.DOTPE
];

/**
 * Loyalty program types. Only a `point` program supports POS points redemption — see
 * `canRedeemPointsOnPos` in `packages/core/loyalty/provider.ts`.
 */
export const LOYALTY_PROGRAM_TYPE = {
  POINT: "point",
} as const;

/**
 * Where a sale originated.
 *
 * Was WEB-ONLY in `index.desktop.ts` until 2026-09-07 — mobile's `data/constants.ts` defines no
 * such constant (grepped, per hazard 32). It moved here for the same reason the loyalty
 * constants above did: a module BOTH platforms run now reads it. `loyaltyRefundEffect` branches
 * on `API` to leave API-sourced sales alone, and that effect stopped being web-only when mobile
 * started running the shared loyalty flow.
 *
 * ⚠️ A frozen map of strings with no platform in it — there was never a reason for this to be
 * per-platform beyond which app happened to define it. Left unresolved it is worse than absent:
 * a variant that does not export it makes `SALE_SOURCE_TYPES.API` a read on `undefined`, which
 * `tsc` cannot see through the folder import and which fails at runtime on that platform only.
 *
 * `calculateAndSaveSaleData` (web) also branches on `API`, to skip both the bill-discount
 * retention filter and the unapplied-coupon sweep.
 */
export const SALE_SOURCE_TYPES = {
  API: "API",
  RISTA: "Rista",
  SUPPORT: "Support",
  SWIGGY: "swiggy",
  ZOMATO: "zomato",
} as const;

/** A sale's `transactionType`. Web-only until the feature gate moved to core (the Re-Open rule reads it). */
export const SALE_TRANSACTION_TYPES = {
  SALE: "Sale",
  RETURN: "Return",
} as const;

/** v1 `printSale`'s no-printer warning. Web-only until `@rista/core/printing`'s `toastSkip` needed it (My Orders v2, 2026-10-08). */
export const DEFAULT_PRINTER_MSG = "A default printer must be selected.";
/**
 * The checkout's back-order warning. Moved from apps/web `data/constants` when the payment engine's
 * mobile host gained the same gate (docs/flows/PAYMENT_ENGINE.md §36), so both apps read one string.
 */
export const BACK_ORDER_MESSAGE =
  "Sale contains back order items, please mark the order as ready to close the sale";
